

import React from 'react';
import { Board } from '../../types';
import { Icons } from '../../constants';
import { THEME_STYLES } from '../../constants/theme';
import { SectionTitle } from './HomeUI';
import { motion } from 'framer-motion';

interface RecentlyViewedProps {
    boards: Board[];
    onSelectBoard: (id: string) => void;
}

// Helper for relative time (simple implementation to avoid heavy libs)
const getTimeAgo = (date: Date | string) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
};

export const RecentlyViewed: React.FC<RecentlyViewedProps> = ({ boards, onSelectBoard }) => {
    // Sort by lastEdited and take top 4
    const recentBoards = [...boards]
        .sort((a, b) => new Date(b.lastEdited).getTime() - new Date(a.lastEdited).getTime())
        .slice(0, 4);

    if (recentBoards.length === 0) return null;

    return (
        <div className="mb-12">
            <SectionTitle title="Jump Back In" subtitle="Pick up right where you left off" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {recentBoards.map((board, i) => {
                    const theme = THEME_STYLES[board.themeColor as keyof typeof THEME_STYLES] || THEME_STYLES.blue;
                    const timeAgo = getTimeAgo(board.lastEdited);

                    // Map theme to raw colors for borders/bg
                    const themeColorMap: Record<string, string> = {
                        blue: 'group-hover:border-blue-500/30 group-hover:bg-blue-50/50 dark:group-hover:bg-blue-500/5',
                        purple: 'group-hover:border-purple-500/30 group-hover:bg-purple-50/50 dark:group-hover:bg-purple-500/5',
                        green: 'group-hover:border-green-500/30 group-hover:bg-green-50/50 dark:group-hover:bg-green-500/5',
                        amber: 'group-hover:border-amber-500/30 group-hover:bg-amber-50/50 dark:group-hover:bg-amber-500/5',
                        rose: 'group-hover:border-rose-500/30 group-hover:bg-rose-50/50 dark:group-hover:bg-rose-500/5',
                    };

                    const themeClasses = themeColorMap[board.themeColor] || themeColorMap.blue;

                    return (
                        <motion.div
                            key={board.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * i }}
                            onClick={() => onSelectBoard(board.id)}
                            className={`
                                group relative p-4 rounded-xl cursor-pointer min-h-[120px]
                                bg-white dark:bg-[#121212]
                                border border-slate-200/60 dark:border-white/5
                                ${themeClasses}
                                transition-all duration-200 transform hover:-translate-y-0.5
                                flex flex-col justify-between
                            `}
                        >
                            {/* Top Row: Icon & Meta */}
                            <div className="flex items-start justify-between mb-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.blob.replace('bg-', 'bg-opacity-10 bg-')} transition-transform group-hover:scale-105 duration-300`}>
                                    <Icons.FileText className={`w-4 h-4 ${theme.text}`} />
                                </div>
                                <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-600 bg-slate-50 dark:bg-white/5 px-1.5 py-0.5 rounded border border-slate-100 dark:border-white/5">
                                    {timeAgo}
                                </span>
                            </div>

                            {/* Bottom Row: Title & Stats */}
                            <div>
                                <h3 className="text-[13px] font-bold text-slate-900 dark:text-slate-100 truncate mb-1.5 group-hover:text-current transition-colors">
                                    {board.title}
                                </h3>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`w-1 h-1 rounded-full ${theme.text.split(' ')[0].replace('text-', 'bg-')}`} />
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-500">
                                            {board.notes?.length || 0} items
                                        </span>
                                    </div>
                                    {/* Optional: Add more stats here if available, e.g. "Shared" or "0 collaborators" */}
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};
